import { supabase } from '@/lib/supabaseClient';
import { PHASE_MAX_INDEX } from '@/lib/jobPhases';
import { fetchSettingsByKey } from '@/lib/settingsData';
import { CLIENT_PORTAL_KEYS } from '@/lib/clientPortalData';
import { flattenInvoices } from '@/lib/invoicesData';
import { fetchScheduleRange, resolveAssignmentJob, isoDate } from '@/lib/scheduleData';

/**
 * Parse a contractor-style date string into a Date.
 * Accepts formats like:
 *   - 'Apr 7, 2026'
 *   - 'Apr 7'         (assumes current year)
 *   - 'TBD' / ''       (returns null)
 *   - ISO strings     ('2026-04-11T...')
 */
export const parseContractorDate = (input) => {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed || /^tbd$/i.test(trimmed)) return null;

  // ISO or anything Date.parse handles cleanly
  const direct = Date.parse(trimmed);
  if (!Number.isNaN(direct)) return new Date(direct);

  // 'Apr 7' style without year -> assume current year
  const withYear = `${trimmed}, ${new Date().getFullYear()}`;
  const fallback = Date.parse(withYear);
  if (!Number.isNaN(fallback)) return new Date(fallback);

  return null;
};

/**
 * Format a number as currency. Treats nullish/NaN as 0.
 */
export const formatCurrency = (value, opts = {}) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: opts.fraction ?? 0,
    minimumFractionDigits: opts.fraction ?? 0,
  }).format(safe);
};

/**
 * Format a Date for the activity feed.
 */
export const formatActivityDate = (date) => {
  if (!date || Number.isNaN(date?.getTime?.())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const sumInvoices = (invoices, predicate = () => true) =>
  (invoices || []).reduce((sum, inv) => {
    if (!predicate(inv)) return sum;
    const amount = Number(inv?.amount) || 0;
    return sum + amount;
  }, 0);

/**
 * Compute dashboard KPIs from raw jobs rows.
 */
export const computeKpis = (jobs = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let activeJobs = 0;
  let totalInvoiced = 0;
  let collected = 0;
  let overdue = 0;
  let upcomingCount = 0;
  let paidCount = 0;
  let invoiceCount = 0;
  let totalContract = 0;

  for (const job of jobs) {
    if (job?.status === 'active') activeJobs += 1;
    totalContract += Number(job?.contract) || 0;

    const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
    invoiceCount += invoices.length;
    totalInvoiced += sumInvoices(invoices);
    collected += sumInvoices(invoices, (i) => i?.status === 'paid');

    for (const inv of invoices) {
      const status = inv?.status;
      const amount = Number(inv?.amount) || 0;
      if (status === 'paid') {
        paidCount += 1;
        continue;
      }
      if (status === 'upcoming') upcomingCount += 1;

      const dueDate = parseContractorDate(inv?.due);
      if (dueDate && dueDate < today) {
        overdue += amount;
      }
    }
  }

  return {
    activeJobs,
    totalInvoiced,
    collected,
    overdue,
    invoiceCount,
    paidCount,
    upcomingCount,
    totalContract,
    outstanding: Math.max(totalInvoiced - collected, 0),
  };
};

/**
 * Compute average projected margin across active jobs using embedded
 * data only (no extra queries). Falls back gracefully when revenue is 0.
 *   bucket: 'healthy'  → ≥ 25%   (green)
 *           'watch'    → ≥ 10%   (amber)
 *           'at_risk'  → <  10%  (rose)
 *           'unknown'  → no active jobs with revenue (neutral)
 */
export const computeMarginHealth = (jobs = []) => {
  const margins = [];
  for (const job of jobs) {
    if (job?.status !== 'active') continue;
    const contract = Number(job?.contract) || 0;
    const coTotal = Number(job?.co_total) || 0;
    const revenue = contract + coTotal;
    if (revenue <= 0) continue;
    let materials = Number(job?.materials_spent) || 0;
    if (!materials && Array.isArray(job?.material_log)) {
      materials = job.material_log.reduce((s, r) => s + (Number(r?.amount) || 0), 0);
    }
    const labor = Number(job?.labor_spent) || 0;
    const cost = materials + labor;
    margins.push(((revenue - cost) / revenue) * 100);
  }
  if (margins.length === 0) {
    return { avgMarginPct: 0, sampleCount: 0, bucket: 'unknown' };
  }
  const avg = margins.reduce((s, n) => s + n, 0) / margins.length;
  let bucket = 'at_risk';
  if (avg >= 25) bucket = 'healthy';
  else if (avg >= 10) bucket = 'watch';
  return { avgMarginPct: avg, sampleCount: margins.length, bucket };
};

/**
 * Build the active-jobs pipeline shape for the Dashboard.
 */
export const buildPipeline = (jobs = []) => {
  return jobs
    .filter((j) => j?.status === 'active')
    .map((job) => {
      const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
      const totalInv = sumInvoices(invoices);
      const paid = sumInvoices(invoices, (i) => i?.status === 'paid');
      const contract = Number(job?.contract) || 0;
      const coTotal = Number(job?.co_total) || 0;
      const totalValue = contract + coTotal;
      const phase = Number(job?.phase) || 0;

      return {
        id: job.id,
        name: job.name || 'Untitled job',
        client: job.client || '\u2014',
        address: job.address || '',
        status: job.status || 'active',
        jobType: job.job_type || 'fixed',
        started: job.started || null,
        estEnd: job.est_end || null,
        contract,
        coTotal,
        totalValue,
        invoicesCount: invoices.length,
        invoicesPaidCount: invoices.filter((i) => i?.status === 'paid').length,
        totalInvoiced: totalInv,
        collected: paid,
        outstanding: Math.max(totalInv - paid, 0),
        phase,
        // Canonical phase scale shared with the Active Jobs module (0..8).
        phaseMax: PHASE_MAX_INDEX,
        progressPct: contract > 0 ? Math.min(100, Math.round((paid / contract) * 100)) : 0,
      };
    })
    .sort((a, b) => b.totalValue - a.totalValue);
};

/**
 * Build a unified activity feed from jobs.notes, clients.last_contact, and
 * client portal signature alerts (proposals signed via the public portal).
 */
export const buildActivityFeed = (jobs = [], clients = [], alerts = [], { limit = 10 } = {}) => {
  const items = [];

  for (const job of jobs) {
    const notes = Array.isArray(job?.notes) ? job.notes : [];
    notes.forEach((note, idx) => {
      const date = parseContractorDate(note?.date);
      items.push({
        kind: 'job-note',
        // Include array index + note.id (when present) to guarantee uniqueness
        // even when the same job has multiple notes with identical date/text prefix
        // (e.g. repeated "Estimate saved" milestone notes on the same day).
        id: `job-${job.id}-${idx}-${note?.id || note?.date || ''}`,
        date,
        rawDate: note?.date || '',
        title: job.name || 'Job update',
        subtitle: job.client || '',
        text: note?.text || '',
        meta: note?.type || 'field',
      });
    });
  }

  clients.forEach((client, idx) => {
    const date = parseContractorDate(client?.last_contact);
    const firstNote =
      Array.isArray(client?.notes) && client.notes.length > 0
        ? String(client.notes[0])
        : '';
    items.push({
      kind: client?.type === 'lead' ? 'lead-touch' : 'client-touch',
      id: `client-${client.id || idx}-${client?.last_contact || ''}`,
      date,
      rawDate: client?.last_contact || '',
      title:
        client?.type === 'lead'
          ? `Lead touch \u2014 ${client?.name || 'Unknown'}`
          : `Client touch \u2014 ${client?.name || 'Unknown'}`,
      subtitle: client?.address || '',
      text: firstNote,
      meta: client?.type || 'client',
    });
  });

  // Signature alerts \u2014 proposals signed by clients via /portal/client/:code.
  // Each alert has { kind:'signature', signed_name, total, estimate_title, created_at, ... }.
  (alerts || []).forEach((alert, idx) => {
    if (!alert || alert.kind !== 'signature') return;
    const date = alert.created_at ? new Date(alert.created_at) : null;
    const formattedTotal = alert.total
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(Number(alert.total) || 0)
      : '';
    items.push({
      kind: 'proposal-signed',
      id: `sig-${alert.id || idx}`,
      date,
      rawDate: alert.created_at || '',
      title: `Proposal signed \u2014 ${alert.estimate_title || 'Estimate'}`,
      subtitle: alert.signed_name || 'Client',
      text: formattedTotal
        ? `Signed for ${formattedTotal} via client portal.`
        : 'Signed via client portal.',
      meta: 'signature',
    });
  });

  items.sort((a, b) => {
    const ta = a.date ? a.date.getTime() : -Infinity;
    const tb = b.date ? b.date.getTime() : -Infinity;
    return tb - ta;
  });

  return items.slice(0, limit);
};

/**
 * Single async loader: fetches jobs + clients + signature alerts in parallel
 * and returns the derived dashboard view.
 */
/**
 * Today's schedule, resolved to job names. Wide-screen dashboard pass
 * (2026-09): "today's schedule ... visible without scrolling" — a compact
 * read of the same `schedule` table the full Schedule page manages.
 */
const buildTodaySchedule = (scheduleRows, jobs) =>
  (scheduleRows || []).map((row) => {
    const { jobName } = resolveAssignmentJob(row, jobs);
    return {
      id: row.id,
      jobName: jobName || 'Unassigned',
      crew: Array.isArray(row.crew) ? row.crew : [],
      note: row.note || '',
    };
  });

/**
 * Unpaid invoices (due + overdue + upcoming, i.e. everything but paid) across
 * every job, soonest-due first. Reuses the same flattening the Invoices page
 * itself uses, so this list and that page always agree — no separate
 * aggregation logic to drift out of sync. Capped for the dashboard tile;
 * the Invoices page is the full list.
 */
const buildUnpaidInvoices = (jobs, { limit = 8 } = {}) =>
  flattenInvoices(jobs)
    .filter((r) => r.status !== 'paid')
    .sort((a, b) => {
      if (a.due_date && b.due_date) return a.due_date - b.due_date;
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return 0;
    })
    .slice(0, limit);

export const fetchDashboardData = async () => {
  const todayIso = isoDate(new Date());
  const [jobsRes, clientsRes, alertsRes, scheduleRows] = await Promise.all([
    supabase
      .from('jobs')
      .select('id,name,client,client_id,address,contract,co_total,phase,status,started,est_end,job_type,materials_spent,notes,change_orders,invoices,created_at')
      .order('created_at', { ascending: false }),
    supabase
      .from('clients')
      .select('id,name,phone,email,address,type,source,ltv,lead_value,lead_type,last_contact,notes,created_at')
      .order('created_at', { ascending: false }),
    fetchSettingsByKey(CLIENT_PORTAL_KEYS.ALERTS, []),
    // Non-fatal: a schedule-fetch hiccup shouldn't take down the whole
    // dashboard load, just leave today's schedule tile empty.
    fetchScheduleRange({ from: todayIso, to: todayIso }).catch(() => []),
  ]);

  if (jobsRes.error) throw jobsRes.error;
  if (clientsRes.error) throw clientsRes.error;

  const jobs = jobsRes.data || [];
  const clients = clientsRes.data || [];
  const alerts = Array.isArray(alertsRes?.value) ? alertsRes.value : [];

  const clientCount = clients.filter((c) => c?.type === 'client').length;
  const leadCount = clients.filter((c) => c?.type === 'lead').length;

  return {
    jobs,
    clients,
    alerts,
    counts: {
      clients: clientCount,
      leads: leadCount,
    },
    kpis: computeKpis(jobs),
    marginHealth: computeMarginHealth(jobs),
    pipeline: buildPipeline(jobs),
    feed: buildActivityFeed(jobs, clients, alerts, { limit: 10 }),
    todaySchedule: buildTodaySchedule(scheduleRows, jobs),
    unpaidInvoices: buildUnpaidInvoices(jobs),
  };
};
